// External Imports
import { Fragment, useEffect, useRef, useState } from "react";
import { requestAdminAPI } from "../../api/posh-api";
import { MdSearch } from "react-icons/md";
import LoadingSpinner from "../UI/LoadingSpinner";

// Internal Imports

/** ========================================================================
 * * CustomerSelector Component
 * Renders a dropdown search component, which allows selecting an option from a list.
 * ========================================================================== */

export default function CustomerSelector({ onSelect, selected, label = "CUSTOMER" }) {
  // Initialize States
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [show, setShow] = useState(false);

  /* ======================= FETCH CUSTOMER ======================= */
  const fetchRecords = async (e) => {
    e.preventDefault();
    const query = e.target.query.value?.toLowerCase();
    if (!query) return;
    try {
      setLoading(true);
      setShow(false);
      const res = await requestAdminAPI("search", {
        table: "customer",
        filters: { query },
      });
      setCustomers(res);
      setShow(true);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= FETCH CUSTOMER ======================= */
  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("fetch", {
        table: "customer",
        id: selected,
        columns: ["plan: customerPlan_on_customer { plan { name order } }"],
      });
      res.plan = res.plan?.plan;
      optionClickHandler(res);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Declare Refs
  const inputRef = useRef();
  const valueRef = useRef();

  /* ======================= NAME FORMATTER ======================= */
  const nameFormatter = (customer) => {
    if (!customer) return "";
    return `${customer.firstName} ${customer.lastName} ${customer.email}`;
  };

  /* ======================= OPTION CLICK HANDLER ======================= */
  const optionClickHandler = (customer) => {
    inputRef.current.value = nameFormatter(customer);
    valueRef.current.value = customer.id;
    setShow(false);
    onSelect(customer);
  };

  // If selection is made outside of the dropdown
  useEffect(() => {
    if (selected) fetchCustomer();
  }, [selected]);

  return (
    <Fragment>
      {show && (
        <div
          onClick={() => setShow(false)}
          className="absolute top-0 left-0 w-full h-full -z-1 no-scrollbar"
        ></div>
      )}
      <div className="relative grid gap-2">
        <form onSubmit={fetchRecords} className="">
          <label className="text-sm text-stone-400">{label}:</label>
          <div className="flex items-center border rounded-md border-stone-600 ">
            <input
              className="relative w-full input"
              autoComplete="off"
              name="query"
              type="text"
              ref={inputRef}
              autoCorrect="off"
              spellCheck="false"
              placeholder="Type here and press enter to search..."
            />
            <button type="submit" className="px-2">
              {loading ? (
                <LoadingSpinner classes="text-primary w-6 h-6" />
              ) : (
                <MdSearch className="icon" />
              )}
            </button>
          </div>
        </form>
        <input
          className="hidden"
          name="customer"
          id="customer"
          ref={valueRef}
        />
        {show && (
          <div className="absolute top-16 py-2 px-4 w-full overflow-scroll rounded-lg bg-stone-700 no-scrollbar max-h-[300px] z-10">
            {customers.map((customer) => (
              <div
                key={customer.id}
                className="px-2 py-1 text-sm font-semibold truncate rounded-md cursor-pointer text-stone-200 hover:bg-stone-600"
                onClick={() => optionClickHandler(customer)}
              >
                {nameFormatter(customer)}
              </div>
            ))}
          </div>
        )}
      </div>
    </Fragment>
  );
}
